import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Users, Clock, CheckCircle, AlertCircle, X, BarChart3, FileText, ClipboardCheck, Trash2, Edit3, Calendar } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDateTime, formatDate } from '../../utils/timeUtils';

interface Assignment {
  id: string;
  paper_id: string;
  paper_title: string;
  deadline: string | null;
  status: 'active' | 'closed';
  created_at: string;
  has_subjective?: boolean; // 是否包含非客观题（判断题+编程题）
  stats: {
    total: number;
    completed: number;
    pending: number;
    expired: number;
    completionRate: number;
    averageScore: number;
  };
}

interface StudentDetail {
  id: string;
  student_id: string;
  username: string;
  status: 'pending' | 'completed' | 'expired';
  score: number | null;
  completed_at: string | null;
  objective_score: number | null;
  programming_score: number | null;
  exam_score: number | null;
  grading_status: string;
  exam_record_id: string | null;
}

interface BoundStudent {
  id: string;
  student_id: string;
  username: string;
  remark: string;
}

export default function AssignmentManage() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [studentDetails, setStudentDetails] = useState<StudentDetail[]>([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [closingAssignment, setClosingAssignment] = useState<string | null>(null);
  const [reopeningAssignment, setReopeningAssignment] = useState<string | null>(null);
  const [deletingAssignment, setDeletingAssignment] = useState<string | null>(null);
  const [selectedAssignments, setSelectedAssignments] = useState<Set<string>>(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [hasPackage, setHasPackage] = useState<boolean>(true);

  // 修改作业相关状态
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
  const [editDeadline, setEditDeadline] = useState<string>('');
  const [boundStudents, setBoundStudents] = useState<BoundStudent[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [savingEdit, setSavingEdit] = useState(false);
  const [studentSearchKeyword, setStudentSearchKeyword] = useState('');

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        // getSession() 可能因 Supabase auth lock 竞争导致 AbortError，重试一次即可
        let session: any = null;
        try {
          const { data } = await supabase.auth.getSession();
          session = data.session;
        } catch (lockError: any) {
          if (lockError?.name === 'AbortError' || lockError?.message?.includes('Lock broken')) {
            // 锁竞争导致的 AbortError，重试一次
            const { data } = await supabase.auth.getSession();
            session = data.session;
          } else {
            throw lockError;
          }
        }
        if (!session || cancelled) return;

        // 并行执行权限检查和作业加载
        await Promise.all([
          checkPackagePermissionWithSession(session),
          loadAssignmentsWithSession(session)
        ]);
      } catch (error) {
        if (!cancelled) {
          console.error('初始化失败:', error);
        }
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, []);

  const checkPackagePermissionWithSession = async (session: any) => {
    try {
      const user = session.user;
      if (!user) return;

      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

      // 统一查 user_packages 表（role='teacher'）
      const { data: userPackages } = await supabase
        .from('user_packages')
        .select('id')
        .eq('user_id', user.id)
        .eq('role', 'teacher')
        .eq('is_active', true)
        .gte('expire_date', today)
        .limit(1);

      setHasPackage(userPackages && userPackages.length > 0);
    } catch (error) {
      console.error('检查刷题包权限失败:', error);
    }
  };

  const loadAssignmentsWithSession = async (session: any) => {
    setLoading(true);
    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'get_teacher_assignments' }),
      });

      const result = await response.json();
      if (result.success) {
        setAssignments(result.data);
      }
    } catch (error) {
      console.error('加载作业列表失败:', error);
    }
    setLoading(false);
  };

  const checkPackagePermission = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

      // 统一查 user_packages 表（role='teacher'）
      const { data: userPackages } = await supabase
        .from('user_packages')
        .select('id')
        .eq('user_id', user.id)
        .eq('role', 'teacher')
        .eq('is_active', true)
        .gte('expire_date', today)
        .limit(1);

      setHasPackage(userPackages && userPackages.length > 0);
    } catch (error) {
      console.error('检查刷题包权限失败:', error);
    }
  };

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action: 'get_teacher_assignments' }),
      });

      const result = await response.json();
      if (result.success) {
        setAssignments(result.data);
      }
    } catch (error) {
      console.error('加载作业列表失败:', error);
    }
    setLoading(false);
  };

  const loadAssignmentDetail = async (assignment: Assignment) => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'get_assignment_stats',
          assignmentId: assignment.id,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setSelectedAssignment(result.data.assignment);
        setStudentDetails(result.data.students);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('加载作业详情失败:', error);
    }
  };

  const handleViewStudentRecord = async (student: StudentDetail) => {
    try {
      // 通过 exam-service 云函数获取学员答题记录（绕过 RLS）
      const session = (await supabase.auth.getSession()).data.session;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/exam-service`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'get_records', studentId: student.student_id }),
      });

      const result = await response.json();
      if (!result.success || !result.records) {
        alert('获取答题记录失败');
        return;
      }

      // 找到对应试卷的答题记录
      const record = result.records.find((r: any) => r.paper_id === selectedAssignment?.paper_id);
      if (!record) {
        alert('未找到该学员的答题记录');
        return;
      }

      // 跳转到 Quiz 错题回顾模式（与学员中心查看详情一致）
      navigate(`/quiz/${record.paper_id}`, {
        state: {
          fromResult: true,
          isProxyView: true,
          studentName: student.username,
          resultData: {
            paperId: record.paper_id,
            paperTitle: record.paper_title,
            score: record.score,
            totalScore: record.total_score,
            timeLeft: record.time_left,
            examDuration: record.exam_duration,
            answers: record.answers || {},
            questions: record.questions || [],
            objectiveScore: record.objective_score,
            objectiveTotalScore: record.objective_total_score,
            programmingScore: record.programming_score,
            questionFeedbacks: record.question_feedbacks || null
          }
        }
      });
    } catch (error) {
      console.error('获取答题记录失败:', error);
      alert('获取答题记录失败，请稍后重试');
    }
  };

  const handleCloseAssignment = async (assignmentId: string) => {
    setClosingAssignment(assignmentId);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'close_assignment',
          assignmentId,
        }),
      });

      const result = await response.json();
      if (result.success) {
        // 更新本地状态
        setAssignments(prev =>
          prev.map(a =>
            a.id === assignmentId ? { ...a, status: 'closed' } : a
          )
        );
        alert('作业已关闭');
      } else {
        alert(result.message || '关闭作业失败');
      }
    } catch (error) {
      console.error('关闭作业失败:', error);
      alert('关闭作业失败，请稍后重试');
    }
    setClosingAssignment(null);
  };

  const handleReopenAssignment = async (assignmentId: string) => {
    setReopeningAssignment(assignmentId);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'reopen_assignment',
          assignmentId,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setAssignments(prev =>
          prev.map(a =>
            a.id === assignmentId ? { ...a, status: 'active' } : a
          )
        );
        alert('作业已重新开启');
      } else {
        alert(result.message || '开启作业失败');
      }
    } catch (error) {
      console.error('开启作业失败:', error);
      alert('开启作业失败，请稍后重试');
    }
    setReopeningAssignment(null);
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    setDeletingAssignment(assignmentId);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'delete_assignment',
          assignmentId,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setAssignments(prev => prev.filter(a => a.id !== assignmentId));
        setSelectedAssignments(prev => {
          const newSet = new Set(prev);
          newSet.delete(assignmentId);
          return newSet;
        });
        alert('作业已删除');
      } else {
        alert(result.message || '删除作业失败');
      }
    } catch (error) {
      console.error('删除作业失败:', error);
      alert('删除作业失败，请稍后重试');
    }
    setDeletingAssignment(null);
    setShowDeleteConfirm(false);
    setAssignmentToDelete(null);
  };

  const handleBatchDelete = async () => {
    if (selectedAssignments.size === 0) {
      alert('请先选择要删除的作业');
      return;
    }
    if (!confirm(`确定要删除选中的 ${selectedAssignments.size} 个作业吗？此操作不可恢复。`)) {
      return;
    }

    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'batch_delete_assignments',
          assignmentIds: Array.from(selectedAssignments),
        }),
      });

      const result = await response.json();
      if (result.success) {
        setAssignments(prev => prev.filter(a => !selectedAssignments.has(a.id)));
        setSelectedAssignments(new Set());
        alert(`已成功删除 ${result.deletedCount || selectedAssignments.size} 个作业`);
      } else {
        alert(result.message || '批量删除失败');
      }
    } catch (error) {
      console.error('批量删除失败:', error);
      alert('批量删除失败，请稍后重试');
    }
  };

  const toggleSelectAssignment = (assignmentId: string) => {
    setSelectedAssignments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(assignmentId)) {
        newSet.delete(assignmentId);
      } else {
        newSet.add(assignmentId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedAssignments.size === assignments.length) {
      setSelectedAssignments(new Set());
    } else {
      setSelectedAssignments(new Set(assignments.map(a => a.id)));
    }
  };

  // 打开修改弹窗
  const handleOpenEditModal = async (assignment: Assignment) => {
    setEditingAssignment(assignment);
    setEditDeadline(assignment.deadline ? assignment.deadline.slice(0, 16) : '');
    setShowEditModal(true);
    setStudentSearchKeyword(''); // 重置搜索关键词

    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session?.user) return;

      // 获取已绑定的学员列表
      const { data: teacherStudents } = await supabase
        .from('teacher_students')
        .select('id, student_id')
        .eq('teacher_id', session.user.id);

      const boundStudentIds = teacherStudents?.map((s: any) => s.student_id) || [];

      if (boundStudentIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, remark')
          .in('id', boundStudentIds);

        setBoundStudents(profiles?.map((p: any) => ({
          id: p.id,
          student_id: p.id,
          username: p.username || '未知',
          remark: p.remark || ''
        })) || []);
      } else {
        setBoundStudents([]);
      }

      // 获取当前作业已分配的学员
      const { data: studentAssignments } = await supabase
        .from('student_assignments')
        .select('student_id')
        .eq('assignment_id', assignment.id);

      const assignedIds = new Set(studentAssignments?.map((s: any) => s.student_id) || []);
      setSelectedStudentIds(assignedIds);
    } catch (error) {
      console.error('加载学员列表失败:', error);
    }
  };

  // 切换学员选择
  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  // 全选/取消全选学员
  const toggleSelectAllStudents = () => {
    if (selectedStudentIds.size === boundStudents.length) {
      setSelectedStudentIds(new Set());
    } else {
      setSelectedStudentIds(new Set(boundStudents.map(s => s.student_id)));
    }
  };

  // 保存修改
  const handleSaveEdit = async () => {
    if (!editingAssignment) return;
    if (selectedStudentIds.size === 0) {
      alert('请至少选择一个学员');
      return;
    }

    setSavingEdit(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'update_assignment',
          assignmentId: editingAssignment.id,
          deadline: editDeadline || null,
          studentIds: Array.from(selectedStudentIds),
        }),
      });

      const result = await response.json();
      if (result.success) {
        // 重新加载作业列表以获取最新数据
        await loadAssignments();
        setShowEditModal(false);
        setEditingAssignment(null);
        alert('作业修改成功');
      } else {
        alert(result.message || '修改作业失败');
      }
    } catch (error) {
      console.error('修改作业失败:', error);
      alert('修改作业失败，请稍后重试');
    }
    setSavingEdit(false);
  };

  // 根据搜索关键词过滤学员列表
  const filteredStudents = boundStudents.filter(student =>
    student.username.toLowerCase().includes(studentSearchKeyword.toLowerCase()) ||
    (student.remark && student.remark.toLowerCase().includes(studentSearchKeyword.toLowerCase()))
  );

  const getStatusBadge = (status: string, deadline: string | null) => {
    if (status === 'closed') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
          已关闭
        </span>
      );
    }
    if (deadline) {
      const deadlineDate = new Date(deadline);
      if (deadlineDate < new Date()) {
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">
            已过期
          </span>
        );
      }
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-600">
        进行中
      </span>
    );
  };

  const getStudentStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <CheckCircle className="w-4 h-4 text-green-500" />
        );
      case 'expired':
        return (
          <AlertCircle className="w-4 h-4 text-gray-400" />
        );
      default:
        return (
          <Clock className="w-4 h-4 text-orange-400" />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">阅卷与评讲</h1>
      </div>

      {!hasPackage && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <p className="text-sm text-amber-700 font-medium">您暂无有效的刷题包</p>
            <p className="text-xs text-amber-600 mt-1">发布、阅卷、评讲功能需要购买刷题包后才能使用</p>
          </div>
          <button
            onClick={() => navigate('/exam-categories')}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
          >
            去购买
          </button>
        </div>
      )}

      {assignments.length > 0 && (
        <div className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedAssignments.size === assignments.length && assignments.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm text-gray-600">
              已选择 {selectedAssignments.size} 个作业
            </span>
          </div>
          {selectedAssignments.size > 0 && (
            <button
              onClick={handleBatchDelete}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              批量删除
            </button>
          )}
        </div>
      )}

      {assignments.length > 0 ? (
        <div className="space-y-4">
          {assignments.map((assignment) => (
            <motion.div
              key={assignment.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedAssignments.has(assignment.id)}
                      onChange={() => toggleSelectAssignment(assignment.id)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold text-gray-800">{assignment.paper_title}</h3>
                    {getStatusBadge(assignment.status, assignment.deadline)}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm mb-3">
                    <div className="flex items-center gap-1 text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>发布于 {formatDate(assignment.created_at)}</span>
                    </div>
                    {assignment.deadline && (
                      <div className="flex items-center gap-1 text-gray-500">
                        <AlertCircle className="w-4 h-4" />
                        <span>截止：{formatDateTime(assignment.deadline)}</span>
                      </div>
                    )}
                    {/* 统计详情 */}
                    <span className="text-green-600">
                      <CheckCircle className="w-4 h-4 inline mr-1" />
                      已完成 {assignment.stats.completed}人
                    </span>
                    <span className={assignment.status === 'closed' ? 'text-gray-500' : 'text-orange-600'}>
                      <Clock className="w-4 h-4 inline mr-1" />
                      {assignment.status === 'closed' ? '未完成' : '待完成'} {assignment.stats.pending}人
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (!assignment.has_subjective) {
                          alert('客观题无需阅卷');
                          return;
                        }
                        navigate(`/teacher/assignments/grading/${assignment.id}`);
                      }}
                      className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        assignment.has_subjective
                          ? 'text-blue-600 hover:bg-blue-50'
                          : 'text-gray-400 cursor-pointer hover:bg-gray-50'
                      }`}
                      title={assignment.has_subjective ? '开始阅卷' : '客观题无需阅卷'}
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      阅卷
                    </button>
                    <button
                      onClick={() => navigate(`/teacher/paper-review/${assignment.paper_id}?assignmentId=${assignment.id}`)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      评讲
                    </button>
                    <button
                      onClick={() => loadAssignmentDetail(assignment)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    >
                      <BarChart3 className="w-4 h-4" />
                      查看详情
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    {assignment.status === 'active' && (
                      <>
                        <button
                          onClick={() => handleOpenEditModal(assignment)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit3 className="w-4 h-4" />
                          修改
                        </button>
                        <button
                          onClick={() => handleCloseAssignment(assignment.id)}
                          disabled={closingAssignment === assignment.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {closingAssignment === assignment.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-600"></div>
                              关闭中...
                            </>
                          ) : (
                            <>
                              <X className="w-4 h-4" />
                              关闭作业
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setAssignmentToDelete(assignment.id);
                            setShowDeleteConfirm(true);
                          }}
                          disabled={deletingAssignment === assignment.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {deletingAssignment === assignment.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                              删除中...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              删除
                            </>
                          )}
                        </button>
                      </>
                    )}
                    {assignment.status === 'closed' && (
                      <>
                        <button
                          onClick={() => handleReopenAssignment(assignment.id)}
                          disabled={reopeningAssignment === assignment.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {reopeningAssignment === assignment.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
                              开启中...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              开启作业
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setAssignmentToDelete(assignment.id);
                            setShowDeleteConfirm(true);
                          }}
                          disabled={deletingAssignment === assignment.id}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {deletingAssignment === assignment.id ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                              删除中...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              删除
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">暂无发布的作业</p>
          <button
            onClick={() => navigate('/teacher/assignments/publish')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            发布第一份作业
          </button>
        </div>
      )}

      {/* 详情弹窗 */}
      <AnimatePresence>
        {showDetailModal && selectedAssignment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-3xl max-h-[80vh] overflow-hidden"
            >
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">{selectedAssignment.paper_title}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      发布于 {formatDate(selectedAssignment.created_at)}
                      {selectedAssignment.deadline && ` · 截止 ${formatDateTime(selectedAssignment.deadline)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* 统计概览 */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="bg-purple-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-purple-600">{studentDetails.length}</p>
                    <p className="text-sm text-gray-600">总人数</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {studentDetails.filter(s => s.status === 'completed').length}
                    </p>
                    <p className="text-sm text-gray-600">已完成</p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {studentDetails.filter(s => s.status === 'pending').length}
                    </p>
                    <p className="text-sm text-gray-600">待完成</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {(() => {
                        const completed = studentDetails.filter(s => s.status === 'completed' && s.score !== null);
                        if (completed.length === 0) return '-';
                        const avg = Math.round(completed.reduce((sum, s) => sum + (s.score || 0), 0) / completed.length);
                        return avg;
                      })()}
                    </p>
                    <p className="text-sm text-gray-600">平均分</p>
                  </div>
                </div>
                
                {/* 学员列表 */}
                <h3 className="font-semibold text-gray-800 mb-3">学员完成情况</h3>
                <div className="space-y-2">
                  {studentDetails.map((student) => {
                    // 判断是否为待阅卷状态（pending）
                    const isPendingGrading = student.grading_status === 'pending';
                    // 客观题得分（自动判分，始终显示）
                    const objectiveScore = student.objective_score !== null && student.objective_score !== undefined
                      ? `${student.objective_score}分`
                      : '-';
                    // 编程题得分（待阅卷时显示"-"）
                    const programmingScore = isPendingGrading ? '-' : (student.programming_score !== null && student.programming_score !== undefined ? `${student.programming_score}分` : '-');
                    // 总分（待阅卷时显示"-"）
                    const totalScore = isPendingGrading ? '-' : (student.exam_score !== null && student.exam_score !== undefined ? `${student.exam_score}分` : '-');

                    return (
                      <div
                        key={student.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-800">{student.username}</span>
                          {/* 状态图标 - 紧跟学员姓名 */}
                          <span className="ml-1">{getStudentStatusIcon(student.status)}</span>
                          {/* 完成时间和查看按钮 */}
                          <div className="flex items-center gap-2">
                            {student.completed_at && (
                              <span className="text-xs text-gray-500">
                                {formatDateTime(student.completed_at)}
                              </span>
                            )}
                            {/* 查看按钮 - 只有完成答题的学员才显示 */}
                            {student.status === 'completed' && (
                              <button
                                onClick={() => handleViewStudentRecord(student)}
                                className="px-2 py-0.5 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                              >
                                查看原卷
                              </button>
                            )}
                          </div>
                        </div>
                        {/* 分数详情 - 横向排列 */}
                        <div className="flex items-center gap-4 text-sm">
                          {/* 客观题得分 */}
                          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded">
                            <span className="text-blue-500">客观题：</span>
                            <span className="font-semibold text-blue-700">{objectiveScore}</span>
                          </div>
                          {/* 编程题得分 */}
                          <div className="flex items-center gap-1 px-2 py-1 bg-purple-50 rounded">
                            <span className="text-purple-500">编程题：</span>
                            <span className="font-semibold text-purple-700">{programmingScore}</span>
                          </div>
                          {/* 总分 */}
                          <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded">
                            <span className="text-green-500">总分：</span>
                            <span className="font-bold text-green-700">{totalScore}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 删除确认弹窗 */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">确认删除</h3>
              </div>
              <p className="text-gray-600 mb-6">
                确定要删除这个作业吗？此操作不可恢复，相关的学员答题记录也将被删除。
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setAssignmentToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={() => assignmentToDelete && handleDeleteAssignment(assignmentToDelete)}
                  disabled={deletingAssignment !== null}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deletingAssignment ? '删除中...' : '确认删除'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 修改作业弹窗 */}
      <AnimatePresence>
        {showEditModal && editingAssignment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
            >
              {/* 弹窗头部 */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">修改作业</h2>
                    <p className="text-sm text-gray-500 mt-1">{editingAssignment.paper_title}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingAssignment(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* 弹窗内容 */}
              <div className="p-6 overflow-y-auto flex-1">
                {/* 截止日期 */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    截止日期（可选）
                  </label>
                  <input
                    type="datetime-local"
                    value={editDeadline}
                    onChange={(e) => setEditDeadline(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                {/* 学员选择 */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      可答卷学员
                    </label>
                    <button
                      onClick={toggleSelectAllStudents}
                      className="text-sm text-purple-600 hover:text-purple-700"
                    >
                      {selectedStudentIds.size === filteredStudents.length ? '取消全选' : '全选'}
                    </button>
                  </div>

                  {/* 搜索框 */}
                  <div className="mb-3">
                    <input
                      type="text"
                      placeholder="搜索学员姓名..."
                      value={studentSearchKeyword}
                      onChange={(e) => setStudentSearchKeyword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                    />
                  </div>

                  {boundStudents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>暂无绑定的学员</p>
                    </div>
                  ) : filteredStudents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>未找到匹配的学员</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                      {filteredStudents.map((student) => (
                        <label
                          key={student.student_id}
                          className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedStudentIds.has(student.student_id)}
                            onChange={() => toggleStudentSelection(student.student_id)}
                            className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-800">{student.username}</span>
                            {student.remark && (
                              <span className="text-xs text-gray-500 ml-2">({student.remark})</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-gray-500 mt-2">
                    已选择 {selectedStudentIds.size} / {boundStudents.length} 名学员
                    {studentSearchKeyword && `（显示 ${filteredStudents.length} 条搜索结果）`}
                  </p>
                </div>
              </div>

              {/* 弹窗底部按钮 */}
              <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingAssignment(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSaveEdit}
                  disabled={savingEdit || selectedStudentIds.size === 0}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {savingEdit ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      保存中...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4" />
                      保存修改
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
